/**
 * The assumption REGISTRY (firm assumptions spec §2.2 AssumptionDefinition,
 * §4.2, §9, §10.1).
 *
 * ONE typed list that the editor, the API validator, the resolver and the
 * report appendix all read. A field does not exist for the product unless it is
 * here, and an `active` definition must name the engine consumer that reads it
 * — `assertRegistryComplete` (and AC-18) refuses a registry where it does not.
 * That is what makes "stored but ignored" impossible to ship quietly.
 *
 * Definitions are code, not data: a firm cannot add a calculation input by
 * sending JSON. Firms add named indices / asset classes through the typed
 * `{id}` templates as those groups are delivered.
 */
/** Bumped whenever a definition is added, removed or its contract changes. */
export const ASSUMPTION_REGISTRY_VERSION = 1;
/** Content schema written by this code (spec §10.3 `schemaVersion`). */
export const ASSUMPTION_CONTENT_SCHEMA_VERSION = 2;
export const ASSUMPTION_CATEGORY_LABELS = Object.freeze({
    economy: 'Economy and inflation',
    assets: 'Asset classes and returns',
    portfolios: 'Portfolios and glidepaths',
    fees: 'Charges and fees',
    income: 'Income and contributions',
    spending: 'Spending, education and care',
    property: 'Property and rent',
    debt: 'Borrowing',
    pensions: 'Pensions and retirement income',
    liquidity: 'Cash reserves and withdrawals',
    longevity: 'Longevity and protection',
    business: 'Business and other assets',
    stress: 'Stress and sensitivity',
});
const ALL_SCOPES = Object.freeze(['set', 'plan', 'scenario', 'item']);
const SET_PLAN_SCENARIO = Object.freeze(['set', 'plan', 'scenario']);
/**
 * Bounds for the LEGACY headline fields are the bounds the existing contract
 * (`firmAssumptionPresetSchema`) already enforces, NOT the wider §4.3 bounds:
 * the legacy engine path was only ever exercised inside them, and §4.3 says
 * legacy content keeps its old validation contract.
 */
const LEGACY_INFLATION = { min: 0, max: 2000 };
const LEGACY_GROWTH = { min: -2000, max: 2000 };
const LEGACY_YIELD = { min: 0, max: 2000 };
const LEGACY_FEE = { min: 0, max: 500 };
/** §4.3 soft plausibility bands. */
const SOFT_INFLATION = { min: -500, max: 2000 };
const SOFT_GROWTH = { min: -5000, max: 5000 };
/**
 * Bounds for the §5.1 index fields (registry version 2).
 *
 * A custom index is a NEW definition, so §4.3's computational bounds apply
 * rather than the legacy contract's — but only as far as the consumer has been
 * exercised. The resolved rate reaches the engine as an escalation rate
 * (`resolveSimpleBps`), the same arithmetic CPI-linked escalation already uses,
 * and the plan-level contract for a custom index (`schemas-household.ts`) has
 * always been ±100% (`bpsSchema.min(-10_000).max(10_000)`). Set-level indices
 * take the SAME range, so a figure that is legal on a plan is legal in a set —
 * a narrower set bound would make a set unable to express what a plan can.
 *
 * A spread is an addition in percentage points, so it is signed on the same
 * range. Floor and cap are bounds on the resolved rate, hence the same range
 * again, with `cap >= floor` checked per instance.
 */
const INDEX_RATE = { min: -10_000, max: 10_000 };
/** §4.3's plausibility band for escalation: outside it warns, never blocks. */
const SOFT_INDEX_RATE = { min: -500, max: 2000 };
/** A lag is a whole number of years; §5.1 caps it at ten. */
const LAG_YEARS = { min: 0, max: 10 };
/** An enum/text/ref field carries no figure; bounds never apply to it. */
const NO_BOUNDS = { min: 0, max: 0 };
/**
 * The `{id}` segment of a collection key (§4.4). Stable, machine-safe and
 * short: it is part of the stored key forever, while the NAME beside it is
 * free text the firm can rename without moving any data.
 */
export const ASSUMPTION_COLLECTION_ID = /^[a-z0-9][a-z0-9-]{0,39}$/;
/** The index fields a set may hold, in editor order. */
export const INDEX_FIELDS = [
    'name',
    'base',
    'rate',
    'spread',
    'floor',
    'cap',
    'rounding',
    'lagYears',
    'tailPolicy',
];
/** §5.1 `indices.{id}.base`, minus the enum option that names another index. */
export const INDEX_BASE_OPTIONS = ['fixed', 'cpi', 'rpi', 'earnings'];
/** §5.1 `indices.{id}.rounding`. Ties away from zero (§6.2). */
export const INDEX_ROUNDING_OPTIONS = ['none', 'nearest10bps', 'nearest100bps'];
/** §5.1 `economy.rpiLinkMode`. */
export const RPI_LINK_MODES = ['fixed', 'linked'];
/**
 * The ready-to-name slots §5.1 asks for. Offered in the editor as one-click
 * additions LINKED TO CPI with that source visible — never pre-populated, and
 * never carrying a specialist rate this product has not got a source for.
 */
export const INDEX_SUGGESTIONS = Object.freeze([
    { id: 'food', name: 'Food' },
    { id: 'utilities', name: 'Utilities' },
    { id: 'transport', name: 'Transport' },
    { id: 'housing-costs', name: 'Housing costs' },
    { id: 'rent', name: 'Rent' },
    { id: 'school-fees', name: 'School fees' },
    { id: 'university-living', name: 'University living costs' },
    { id: 'healthcare', name: 'Healthcare' },
    { id: 'home-care', name: 'Home care' },
    { id: 'residential-care', name: 'Residential care' },
    { id: 'insurance-premiums', name: 'Insurance premiums' },
    { id: 'maintenance', name: 'Maintenance' },
]);
export const LEGACY_ASSET_CLASSES = ['equity', 'bonds', 'cash', 'property'];
const CLASS_LABEL = Object.freeze({
    equity: 'Equity',
    bonds: 'Bonds',
    cash: 'Cash',
    property: 'Property',
});
function economyRate(key, label, description, synonyms) {
    return {
        key,
        label,
        category: 'economy',
        description,
        type: 'RATE',
        unit: 'bps',
        pathAllowed: false,
        indexLinkAllowed: false,
        defaultRule: { kind: 'LEGACY' },
        hardBounds: LEGACY_INFLATION,
        softBounds: SOFT_INFLATION,
        scopes: SET_PLAN_SCENARIO,
        dependsOn: [],
        capability: 'legacy-annual-v1',
        status: 'active',
        consumer: 'engine.resolveAssumptions.scalar',
        reportLabel: label,
        introducedIn: 1,
        synonyms,
    };
}
function classDefinitions(cls) {
    const name = CLASS_LABEL[cls];
    return [
        {
            key: `assets.${cls}.capitalGrowth`,
            label: `${name} capital growth`,
            category: 'assets',
            description: `Nominal annual capital growth of ${name.toLowerCase()} holdings, before fees and excluding distributions.`,
            type: 'RATE',
            unit: 'bps',
            pathAllowed: false,
            indexLinkAllowed: false,
            defaultRule: { kind: 'LEGACY' },
            hardBounds: LEGACY_GROWTH,
            softBounds: SOFT_GROWTH,
            scopes: ALL_SCOPES,
            dependsOn: [],
            capability: 'legacy-annual-v1',
            status: 'active',
            consumer: 'engine.resolveAssumptions.classRates',
            reportLabel: `${name} growth`,
            sensitivity: 'higherRaisesOutcome',
            introducedIn: 1,
            synonyms: [cls, 'return', 'growth'],
        },
        {
            key: `assets.${cls}.incomeYield`,
            label: `${name} income yield`,
            category: 'assets',
            description: `Annual distribution from ${name.toLowerCase()} holdings, held separately from capital growth (split return mode).`,
            type: 'RATE',
            unit: 'bps',
            pathAllowed: false,
            indexLinkAllowed: false,
            defaultRule: { kind: 'LEGACY' },
            hardBounds: LEGACY_YIELD,
            scopes: ALL_SCOPES,
            dependsOn: [],
            capability: 'legacy-annual-v1',
            status: 'active',
            consumer: 'engine.resolveAssumptions.classRates',
            reportLabel: `${name} yield`,
            introducedIn: 1,
            synonyms: [cls, 'yield', 'income', 'dividend', 'interest'],
        },
    ];
}
/**
 * §5.1's RPI linkage, and the three §5.1 economy keys that have NO consumer on
 * the legacy engine path.
 *
 * `rpiLinkMode` / `rpiSpread` are active because the legacy path does consume
 * them — not per year, but at PIN time: `resolveIndexRates` turns
 * "linked, +0.80%" into the single RPI figure the bundle carries, which is
 * exactly what an RPI-escalated item already reads. `fixed` mode stores
 * nothing and behaves as it always has.
 *
 * `baseRate`, `cashDepositSpread` and `reportingDeflator` are GATED: the
 * legacy engine has no reference-rate input (cash growth is a class rate, not
 * a base rate plus a spread) and no selectable deflator (today's-money
 * reporting always deflates by CPI). Registering them active would be a figure
 * an owner could set and nothing would read — exactly what AC-18 exists to
 * prevent. They become active with the Milestone B economy resolver.
 */
const ECONOMY_LINK_DEFINITIONS = Object.freeze([
    {
        key: 'economy.rpiLinkMode',
        label: 'RPI basis',
        category: 'economy',
        description: 'Whether RPI is held as its own figure or derived as CPI plus a spread. Derived means the two move together; changing the basis is a change of assumption, not a display option.',
        type: 'ENUM',
        unit: 'none',
        pathAllowed: false,
        indexLinkAllowed: false,
        defaultRule: { kind: 'OFF' },
        hardBounds: NO_BOUNDS,
        scopes: ['set'],
        dependsOn: [],
        capability: 'legacy-annual-v1',
        status: 'active',
        consumer: 'engine.resolveAssumptions.scalar',
        reportLabel: 'RPI basis',
        introducedIn: 2,
        synonyms: ['rpi', 'link', 'derived', 'basis'],
    },
    {
        key: 'economy.rpiSpread',
        label: 'RPI spread over CPI',
        category: 'economy',
        description: 'Percentage points added to CPI when RPI is derived. Applies only on the derived basis; on the fixed basis RPI carries its own figure.',
        type: 'RATE',
        unit: 'bps',
        pathAllowed: false,
        indexLinkAllowed: false,
        defaultRule: { kind: 'REQ', when: 'economy.rpiLinkMode is linked' },
        hardBounds: INDEX_RATE,
        softBounds: SOFT_INDEX_RATE,
        scopes: ['set'],
        dependsOn: ['economy.cpi', 'economy.rpiLinkMode'],
        capability: 'legacy-annual-v1',
        status: 'active',
        consumer: 'engine.resolveAssumptions.scalar',
        reportLabel: 'RPI spread over CPI',
        introducedIn: 2,
        synonyms: ['rpi', 'spread', 'wedge', 'formula effect'],
    },
    {
        key: 'economy.baseRate',
        label: 'Reference interest rate',
        category: 'economy',
        description: 'The firm’s model of future reference interest rates. Not a live market quote.',
        type: 'RATE',
        unit: 'bps',
        pathAllowed: false,
        indexLinkAllowed: false,
        defaultRule: { kind: 'REQ', when: 'a linked cash or borrowing rate is used' },
        hardBounds: INDEX_RATE,
        scopes: ['set'],
        dependsOn: [],
        capability: 'legacy-annual-v1',
        status: 'gated',
        consumer: null,
        applicabilityExclusion: 'No consumer on the legacy annual path: cash and borrowing rates are entered per holding or per liability, never as a reference rate plus a spread. Activates with the Milestone B economy resolver.',
        reportLabel: 'Reference interest rate',
        introducedIn: 2,
        synonyms: ['base rate', 'bank rate', 'interest'],
    },
    {
        key: 'economy.cashDepositSpread',
        label: 'Cash deposit spread',
        category: 'economy',
        description: 'Percentage points added to the reference rate for linked cash accounts.',
        type: 'RATE',
        unit: 'bps',
        pathAllowed: false,
        indexLinkAllowed: false,
        defaultRule: { kind: 'ZERO' },
        hardBounds: INDEX_RATE,
        scopes: ['set'],
        dependsOn: ['economy.baseRate'],
        capability: 'legacy-annual-v1',
        status: 'gated',
        consumer: null,
        applicabilityExclusion: 'Depends on economy.baseRate, which has no consumer on the legacy annual path.',
        reportLabel: 'Cash deposit spread',
        introducedIn: 2,
        synonyms: ['deposit', 'savings', 'spread'],
    },
    {
        key: 'economy.reportingDeflator',
        label: 'Today’s-money deflator',
        category: 'economy',
        description: 'Which index today’s-money figures are deflated by. CPI unless another approved index is chosen.',
        type: 'REF',
        unit: 'none',
        pathAllowed: false,
        indexLinkAllowed: false,
        defaultRule: { kind: 'LINK', target: 'economy.cpi' },
        hardBounds: NO_BOUNDS,
        scopes: ['set'],
        dependsOn: ['economy.cpi'],
        capability: 'legacy-annual-v1',
        status: 'gated',
        consumer: null,
        applicabilityExclusion: 'The engine deflates by CPI with no selectable alternative, so a choice here would be stored and ignored. Activates when the deflator becomes an input.',
        reportLabel: 'Today’s-money deflator',
        introducedIn: 2,
        synonyms: ['deflator', 'in today’s terms', 'purchasing power'],
    },
]);
/** One index-template field, with the boilerplate every one of them shares. */
function indexField(field, def) {
    const gated = def.status === 'gated';
    return {
        key: `indices.{id}.${field}`,
        label: def.label,
        category: 'economy',
        description: def.description,
        type: def.type,
        unit: def.unit,
        pathAllowed: false,
        indexLinkAllowed: false,
        defaultRule: def.defaultRule ?? { kind: 'OFF' },
        hardBounds: def.hardBounds,
        ...(def.softBounds !== undefined ? { softBounds: def.softBounds } : {}),
        scopes: ['set'],
        dependsOn: [],
        capability: 'legacy-annual-v1',
        status: gated ? 'gated' : 'active',
        consumer: gated ? null : 'engine.resolveAssumptions.customIndices',
        ...(def.applicabilityExclusion !== undefined
            ? { applicabilityExclusion: def.applicabilityExclusion }
            : {}),
        reportLabel: def.label,
        introducedIn: 2,
        synonyms: def.synonyms,
    };
}
/**
 * The §5.1 custom-index TEMPLATE (§4.4 `{id}`). One definition per FIELD, not
 * per index: a firm instantiates `indices.<id>.<field>` for each index it
 * names, and `assumptionDefinitionFor` resolves a concrete key back to the
 * template that governs it.
 *
 * `lagYears` and `tailPolicy` are gated. Both need a per-YEAR resolver — a
 * prior-year base figure, and behaviour past the last dated segment — and the
 * legacy annual path resolves one flat rate per index at pin time (D-03/D-05).
 * Registering them active would store an instruction nothing carries out.
 */
const INDEX_TEMPLATE_DEFINITIONS = Object.freeze([
    indexField('name', {
        label: 'Index name',
        description: 'What this index is called wherever it is chosen — on an expense, and in the report’s assumptions table. Unique within the set.',
        type: 'TEXT',
        unit: 'none',
        hardBounds: NO_BOUNDS,
        defaultRule: { kind: 'REQ' },
        synonyms: ['index', 'name', 'series'],
    }),
    indexField('base', {
        label: 'Based on',
        description: 'What this index follows: its own fixed rate, CPI, RPI, earnings, or another index in this set.',
        type: 'ENUM',
        unit: 'none',
        hardBounds: NO_BOUNDS,
        defaultRule: { kind: 'REQ' },
        synonyms: ['base', 'follows', 'linked to', 'cpi', 'rpi'],
    }),
    indexField('rate', {
        label: 'Fixed rate',
        description: 'The index’s own annual rate. Required when it is not based on another index.',
        type: 'RATE',
        unit: 'bps',
        hardBounds: INDEX_RATE,
        softBounds: SOFT_INDEX_RATE,
        defaultRule: { kind: 'REQ', when: 'indices.{id}.base is fixed' },
        synonyms: ['rate', 'escalation', 'fixed'],
    }),
    indexField('spread', {
        label: 'Spread',
        description: 'Percentage points added to whatever this index is based on, before any floor or cap. Zero unless entered.',
        type: 'RATE',
        unit: 'bps',
        hardBounds: INDEX_RATE,
        softBounds: SOFT_INDEX_RATE,
        defaultRule: { kind: 'ZERO' },
        synonyms: ['spread', 'plus', 'margin', 'uplift'],
    }),
    indexField('floor', {
        label: 'Floor',
        description: 'Lowest rate this index can resolve to. Absent means no floor.',
        type: 'RATE',
        unit: 'bps',
        hardBounds: INDEX_RATE,
        synonyms: ['floor', 'minimum', 'collar'],
    }),
    indexField('cap', {
        label: 'Cap',
        description: 'Highest rate this index can resolve to. Absent means no cap; a cap must be at least the floor.',
        type: 'RATE',
        unit: 'bps',
        hardBounds: INDEX_RATE,
        synonyms: ['cap', 'maximum', 'collar', 'ceiling'],
    }),
    indexField('rounding', {
        label: 'Rounding',
        description: 'Optional rounding of the resolved rate before it is used. Ties round away from zero.',
        type: 'ENUM',
        unit: 'none',
        hardBounds: NO_BOUNDS,
        synonyms: ['rounding', 'round', 'nearest'],
    }),
    indexField('lagYears', {
        label: 'Lag (years)',
        description: 'Use the base index’s figure from a given number of years earlier. Needs a per-year series, including the years before the projection starts.',
        type: 'COUNT',
        unit: 'years',
        hardBounds: LAG_YEARS,
        status: 'gated',
        applicabilityExclusion: 'A lag needs a per-year index series and pre-start history; the legacy annual path resolves one flat rate per index at pin time (D-05). Activates with the Milestone B economy resolver.',
        synonyms: ['lag', 'prior year', 'look-back'],
    }),
    indexField('tailPolicy', {
        label: 'Beyond the last segment',
        description: 'What happens after an index’s final dated segment: hold the last figure, or require the path to be extended.',
        type: 'ENUM',
        unit: 'none',
        hardBounds: NO_BOUNDS,
        status: 'gated',
        applicabilityExclusion: 'Only meaningful for a dated PATH, which the legacy annual path does not resolve per year (D-05). Activates with the Milestone B economy resolver.',
        synonyms: ['tail', 'after', 'extend'],
    }),
]);
const DEFINITIONS = Object.freeze([
    economyRate('economy.cpi', 'CPI inflation', 'General price inflation and the default deflator for today’s-money figures.', ['inflation', 'prices', 'cpi']),
    economyRate('economy.rpi', 'RPI inflation', 'A separate RPI series for RPI-linked amounts. Held as its own figure, never forced to CPI plus a spread.', ['inflation', 'rpi', 'retail prices']),
    economyRate('economy.earnings', 'Earnings growth', 'Nominal earnings growth used by wage-linked amounts.', ['wages', 'salary growth', 'earnings']),
    ...LEGACY_ASSET_CLASSES.flatMap(classDefinitions),
    {
        key: 'fees.legacyDrag',
        label: 'Default fee',
        category: 'fees',
        description: 'Aggregate annual platform and fund charge applied where a holding carries no fee of its own. Compatibility-mode fee: it is never added on top of component fees.',
        type: 'RATE',
        unit: 'bps',
        pathAllowed: false,
        indexLinkAllowed: false,
        defaultRule: { kind: 'LEGACY' },
        hardBounds: LEGACY_FEE,
        scopes: ['set'],
        dependsOn: [],
        capability: 'legacy-annual-v1',
        status: 'active',
        consumer: 'engine.resolveAssumptions.feeDrag',
        reportLabel: 'Default fee',
        sensitivity: 'higherLowersOutcome',
        introducedIn: 1,
        synonyms: ['charges', 'fees', 'platform', 'fund', 'ocf'],
    },
    ...ECONOMY_LINK_DEFINITIONS,
    ...INDEX_TEMPLATE_DEFINITIONS,
]);
const BY_KEY = new Map(DEFINITIONS.map((d) => [d.key, d]));
export function assumptionDefinitions() {
    return DEFINITIONS;
}
export function assumptionDefinition(key) {
    return BY_KEY.get(key);
}
/** `indices.<id>.<field>` split into its parts, or null. */
export function parseIndexKey(key) {
    const parts = key.split('.');
    if (parts.length !== 3 || parts[0] !== 'indices')
        return null;
    const [, id, field] = parts;
    if (!ASSUMPTION_COLLECTION_ID.test(id))
        return null;
    if (!INDEX_FIELDS.includes(field))
        return null;
    return { id, field: field };
}
/**
 * The definition governing a key, INCLUDING a collection instance: every
 * `indices.<id>.spread` is governed by the one `indices.{id}.spread`
 * template, with the concrete key substituted so an error message names the
 * field the owner is looking at (§4.4).
 *
 * This — not `assumptionDefinition` — is what validation and the editor use:
 * one field list, however many indices a firm names.
 */
export function assumptionDefinitionFor(key) {
    const exact = BY_KEY.get(key);
    if (exact !== undefined)
        return exact;
    const parsed = parseIndexKey(key);
    if (parsed === null)
        return undefined;
    const template = BY_KEY.get(`indices.{id}.${parsed.field}`);
    return template === undefined ? undefined : { ...template, key };
}
/** The template definitions for the index collection, in editor order. */
export function indexFieldDefinitions() {
    return INDEX_TEMPLATE_DEFINITIONS;
}
/**
 * Every key a complete LEGACY-capability set must hold to be publishable.
 * Template keys are excluded: an index is optional, and a set that names none
 * is complete (§5.1 asks for slots to offer, never rows to insist on).
 */
export function requiredLegacyKeys() {
    return DEFINITIONS.filter((d) => d.status === 'active' && d.defaultRule.kind === 'LEGACY' && parseTemplateKey(d.key) === null).map((d) => d.key);
}
/** `{id}`-bearing keys are templates, never storable keys in their own right. */
export function parseTemplateKey(key) {
    const m = /^([a-z]+)\.\{id\}\.(.+)$/.exec(key);
    return m === null ? null : { collection: m[1], field: m[2] };
}
/** Label / synonym / key search used by the editor (§4.1). */
export function searchAssumptionDefinitions(query) {
    const q = query.trim().toLowerCase();
    if (q === '')
        return DEFINITIONS;
    return DEFINITIONS.filter((d) => d.key.toLowerCase().includes(q) ||
        d.label.toLowerCase().includes(q) ||
        d.synonyms.some((s) => s.toLowerCase().includes(q)));
}
/**
 * Registry self-check (§9): an active definition names a consumer or declares
 * why it has none; a gated one names no consumer it does not have; keys are
 * unique; dependencies point at registered keys. Returns problems rather than
 * throwing so the test can print all of them at once.
 */
export function registryProblems(defs = DEFINITIONS) {
    const problems = [];
    const seen = new Set();
    for (const d of defs) {
        if (seen.has(d.key))
            problems.push(`${d.key}: duplicate key`);
        seen.add(d.key);
        if (d.status === 'active' && d.consumer === null && d.applicabilityExclusion === undefined) {
            problems.push(`${d.key}: active with neither a consumer nor an applicability exclusion`);
        }
        if (d.hardBounds.min > d.hardBounds.max)
            problems.push(`${d.key}: inverted hard bounds`);
        // A soft band may be wider than a tight legacy hard band (it then simply
        // never fires on that side); it must never be inverted.
        if (d.softBounds !== undefined && d.softBounds.min > d.softBounds.max) {
            problems.push(`${d.key}: inverted soft bounds`);
        }
        if (d.scopes.length === 0)
            problems.push(`${d.key}: no permitted scope`);
    }
    for (const d of defs) {
        for (const dep of d.dependsOn) {
            if (!seen.has(dep))
                problems.push(`${d.key}: depends on unregistered ${dep}`);
        }
    }
    return problems;
}
//# sourceMappingURL=registry.js.map